import { useRef } from 'react'
import {
  FormControl,
  FormGroup,
  FormControlLabel,
  Box,
  Divider,
  RadioGroup,
  Typography
} from '@mui/material'
import { GetMenuItem } from '__generated__/types'
import BelugaCheckbox from 'components/BelugaCheckbox'
import BelugaRadio from 'components/BelugaRadio'
import ValidationIndicator from 'components/ValidationIndicator'
import { usd } from 'utils/currency'
import { getModifierOptionsText } from 'utils/menus'
import { useTracking } from 'contexts/Tracking/TrackingContext'
import { useRouter } from 'next/router'

interface Props {
  menuItemId: string
  modifierGroup: GetMenuItem.SubMenuItems
  updateFormData: any
  subFormData: any
  formSentTriggered: boolean
}

const ModifierGroup = ({
  menuItemId,
  modifierGroup,
  updateFormData,
  subFormData,
  formSentTriggered
}: Props): JSX.Element => {
  const router = useRouter()
  const { merchantLocationId } = router.query
  const { tracking } = useTracking()
  const valueCount = Array.isArray(subFormData) ? subFormData.length : 0
  const wasValueInRangeRef = useRef(false)
  const itemsSelectedCount = subFormData ? subFormData.length : 0
  const min = modifierGroup.min || 0
  const max = modifierGroup.max || 0
  const showRadio = min === 1 && max === 1
  const showCheckboxes = !showRadio
  const optionsText = getModifierOptionsText(min, max)

  if (!wasValueInRangeRef.current) {
    if (modifierGroup.max === 0) {
      wasValueInRangeRef.current = itemsSelectedCount >= min
    } else {
      wasValueInRangeRef.current =
        itemsSelectedCount >= min && itemsSelectedCount <= max
    }
  }

  const countValidationError =
    (formSentTriggered || wasValueInRangeRef.current) &&
    (itemsSelectedCount < min ||
      (!!modifierGroup.max &&
        modifierGroup.max > 0 &&
        itemsSelectedCount > max))

  return (
    <>
      <Divider sx={{ marginTop: '10px', marginBottom: '16px' }} />
      <Box display="flex" justifyContent="space-between">
        <Typography component="h4" variant="h4" gutterBottom>
          {modifierGroup.name}
        </Typography>
        {min > 0 && !countValidationError && (
          <ValidationIndicator
            id={menuItemId}
            error={valueCount < min}
            label="REQUIRED"
          />
        )}
      </Box>
      {optionsText && (
        <Typography
          component="p"
          variant="body2"
          className={
            countValidationError ? `form-validation-label-${menuItemId}` : ''
          }
          color={countValidationError ? 'error' : 'textSecondary'}
          gutterBottom
        >
          {optionsText}
        </Typography>
      )}
      {showRadio && (
        <FormControl
          sx={{ width: '100%' }}
          component="fieldset"
          onChange={(event: any) => {
            const selectedSubMenuItem = modifierGroup.subMenuItems?.find(
              (item) => item.id === event.target.value
            )
            if (selectedSubMenuItem) {
              tracking.log('WEB:MENU_ITEM_MODIFIER_ADDED', {
                merchantLocationId,
                menuItemId,
                subMenuItem: {
                  id: selectedSubMenuItem.id,
                  name: selectedSubMenuItem.name,
                  amount: selectedSubMenuItem.price
                }
              })
            }
            updateFormData(modifierGroup.id, [event.target.value])
          }}
        >
          <RadioGroup name={modifierGroup.id} value={subFormData?.[0]}>
            {modifierGroup.subMenuItems.map((modifier) => (
              <Box
                key={modifier.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <FormControlLabel
                  key={modifier.id}
                  control={<BelugaRadio />}
                  value={modifier.id}
                  label={modifier.name}
                />
                {modifier.price > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="textSecondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    + {usd(modifier.price)}
                  </Typography>
                )}
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
      )}
      {showCheckboxes && (
        <FormControl
          sx={{ width: '100%' }}
          component="fieldset"
          onChange={(event: any) => {
            const values = subFormData ? subFormData : []
            if (event.target.checked) {
              const selectedSubMenuItem = modifierGroup.subMenuItems?.find(
                (item) => item.id === event.target.value
              )
              if (selectedSubMenuItem) {
                tracking.log('WEB:MENU_ITEM_MODIFIER_ADDED', {
                  merchantLocationId,
                  menuItemId,
                  subMenuItem: {
                    id: selectedSubMenuItem.id,
                    name: selectedSubMenuItem.name,
                    amount: selectedSubMenuItem.price
                  }
                })
              }
              updateFormData(modifierGroup.id, [...values, event.target.value])
            } else {
              updateFormData(
                modifierGroup.id,
                values.filter(
                  (value: string) =>
                    value !== event.target.value ||
                    (value === event.target.value && event.target.checked)
                )
              )
            }
          }}
        >
          <FormGroup>
            {modifierGroup.subMenuItems.map((modifier) => (
              <Box
                key={modifier.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <FormControlLabel
                  control={
                    <BelugaCheckbox
                      name={modifier.id}
                      defaultChecked={subFormData?.includes(modifier.id)}
                      invalid={countValidationError}
                    />
                  }
                  value={modifier.id}
                  label={modifier.name}
                  sx={{ flex: 1, marginRight: 24 }}
                />
                {modifier.price > 0 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="textSecondary"
                  >
                    + {usd(modifier.price)}
                  </Typography>
                )}
              </Box>
            ))}
          </FormGroup>
        </FormControl>
      )}
    </>
  )
}

export default ModifierGroup
